import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../supabaseClient";

const AuthContext = createContext(null);

/** Shared role normalization for routes and profile rows */
export function normalizeAuthRole(r) {
  if (r == null || r === "") return null;
  const normalized = String(r).toLowerCase().trim().replace(/\s+/g, "_");
  if (normalized === "jobseeker") return "job_seeker";
  return normalized;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cache of the user id whose role we've already fetched, so the same
  // profiles.role lookup never runs twice for the same session.
  const cachedRoleUserIdRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchRoleForUser = useCallback(async (authUser) => {
    if (!authUser?.id) {
      cachedRoleUserIdRef.current = null;
      if (isMountedRef.current) setRole(null);
      return;
    }

    if (cachedRoleUserIdRef.current === authUser.id) {
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", authUser.id)
        .maybeSingle();

      if (!isMountedRef.current) return;

      if (error) {
        console.warn("[AuthContext] profile role lookup failed:", error.message);
        setRole(null);
        return;
      }

      cachedRoleUserIdRef.current = authUser.id;
      setRole(normalizeAuthRole(profile?.role));
    } catch (e) {
      if (!isMountedRef.current) return;
      console.warn("[AuthContext] profile role lookup error:", e?.message || e);
      setRole(null);
    }
  }, []);

  const applySession = useCallback(
    async (session, { markLoaded } = {}) => {
      if (!isMountedRef.current) return;

      const sessionUser = session?.user || null;
      setUser(sessionUser);

      if (sessionUser) {
        await fetchRoleForUser(sessionUser);
      } else {
        cachedRoleUserIdRef.current = null;
        if (isMountedRef.current) setRole(null);
      }

      if (markLoaded && isMountedRef.current) {
        setLoading(false);
      }
    },
    [fetchRoleForUser],
  );

  const refreshSession = useCallback(async () => {
    // Force a role refetch on next applySession by invalidating the cache.
    cachedRoleUserIdRef.current = null;
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn("[AuthContext] refreshSession getSession error:", error.message);
    }
    await applySession(data?.session ?? null, { markLoaded: false });
  }, [applySession]);

  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          console.warn("[AuthContext] bootstrap getSession error:", error.message);
        }
        await applySession(data?.session ?? null, { markLoaded: true });
      } catch (e) {
        if (cancelled) return;
        console.warn("[AuthContext] bootstrap error:", e?.message || e);
        if (isMountedRef.current) {
          setUser(null);
          setRole(null);
          setLoading(false);
        }
      }
    };

    bootstrap();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION fires synchronously after subscribing and would duplicate bootstrap.
      if (event === "INITIAL_SESSION") return;

      // USER_UPDATED can imply a role change in profiles — invalidate the cache.
      if (event === "USER_UPDATED") {
        cachedRoleUserIdRef.current = null;
      }

      // For SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY:
      // apply the session payload directly, no extra getUser()/getSession() round trip.
      applySession(session ?? null, { markLoaded: false });
    });

    return () => {
      cancelled = true;
      isMountedRef.current = false;
      try {
        listener?.subscription?.unsubscribe?.();
      } catch (e) {
        console.warn("[AuthContext] unsubscribe error:", e?.message || e);
      }
    };
  }, [applySession]);

  const value = useMemo(
    () => ({
      user,
      role,
      loading,
      loadingAuth: loading,
      isAuthed: !!user,
      refreshSession,
    }),
    [user, role, loading, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
